import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Box,
  Typography,
  Divider,
  Avatar,
  Tooltip
} from '@mui/material';
import { keyframes } from '@mui/system';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import InventoryIcon from '@mui/icons-material/Inventory';
import BookIcon from '@mui/icons-material/Book';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArchiveIcon from '@mui/icons-material/Archive';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import StoreIcon from '@mui/icons-material/Store';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGuide } from '../context/GuideContext';
import { clearSession } from '../utils/clearSession';

const guidePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(126,196,250,0.75); }
  70%  { box-shadow: 0 0 0 10px rgba(126,196,250,0); }
  100% { box-shadow: 0 0 0 0 rgba(126,196,250,0); }
`;

const drawerWidth = 220;
const mobileDrawerWidth = 180;

// Helper function to check if user can access a feature
const canAccessFeature = (featurePath) => {
  try {
    const uiPreferences = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
    const userRole = localStorage.getItem('role');
    const dreftTamer = false
    
    // Role-based access rules
    if (userRole === 'student') {
      // Students can only access their schedule and academic staff
      const studentAccessible = [
        '/dashboard',
        '/subjects', // For viewing subjects
        '/educational-schedule', // For viewing schedule
        '/reset-password',
        // Add academic routes when available: '/exams', '/assignments', '/grades', '/attendance'
      ];
      return studentAccessible.includes(featurePath);
    }
    
    if (userRole === 'parent') {
      // Parents can only read their children's academic data
      const parentAccessible = [
        '/dashboard',
        '/students', // For viewing their children's profiles
        '/subjects', // For viewing subjects
        '/educational-schedule', // For viewing schedule
        '/reset-password',
        // Add academic routes when available: '/exams', '/assignments', '/grades', '/attendance' (read-only)
      ];
      return parentAccessible.includes(featurePath);
    }
    
    if (userRole === 'teacher') {
      // Teachers can access academic features but not financial/admin/staff
      const teacherAccessible = [
        '/dashboard',
        '/teacher-dashboard', // Teacher-specific dashboard
        '/students', // For viewing students (academic only)
        '/classes', // Only assigned classes (filtered in backend)
        '/subjects', // For managing subjects
        '/educational-schedule', // For viewing/editing schedule
        '/events', // Events management
        '/reset-password',
        // Add academic routes when available: '/exams', '/assignments', '/grades', '/attendance'
      ];
      
      // Check if feature is in teacher accessible list
      if (teacherAccessible.includes(featurePath)) {
        // Also check UI preferences for school features
        if (featurePath === '/educational-schedule') {
          return !!uiPreferences?.school?.educational_schedule;
        }
        if (featurePath === '/classes' || featurePath === '/subjects') {
          return uiPreferences?.school?.classes ?? true;
        }
        return true;
      }
      return false;
    }
    
    // Manager and employee roles use existing permission system
    const featurePermissions = {
      // General
      '/dashboard': true, // Always accessible
      
      // Financial (only for manager/employee)
      '/financial': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.financial?.financial_management,
      '/recipient': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.financial?.recipient_management,
      '/finance/cheques-status': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.financial?.payments_schedule,
      '/finance/outgoingcheque': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.financial?.outgoing_cheques,
      
      // School
      '/students': true, // Always accessible
      '/staff': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.school?.staff,
      '/classes': uiPreferences?.school?.classes,
      '/subjects': uiPreferences?.school?.classes, // Subjects uses same permission as classes
      '/educational-schedule': uiPreferences?.school?.educational_schedule,
      '/substitute-teachers': uiPreferences?.school?.classes,
      '/authorized-payers': userRole === 'manager' || userRole === 'employee',
      '/buses': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.school?.buses,
      '/store': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.school?.store,
      '/store-purchases': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.school?.store,
      '/miscellaneous': (userRole === 'manager' || userRole === 'employee') && (uiPreferences?.school?.miscellaneous || uiPreferences?.school?.store),
      '/archivestudents': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.school?.archive,
      '/archivestaff': (userRole === 'manager' || userRole === 'employee') && uiPreferences?.school?.archive,
      '/library': uiPreferences?.school?.library,
      '/events': (userRole === 'manager' || userRole === 'employee' || userRole === 'teacher'),
      
      // Administrative (only for manager/admin)
      '/reports': userRole === 'manager' || userRole === 'admin',
      '/admin/logs': (userRole === 'manager' || userRole === 'admin') && uiPreferences?.administrative?.logs,
      '/admin/users': userRole === 'manager' || userRole === 'admin', // New user management page
      '/admin/year-transition': userRole === 'manager' || userRole === 'admin',
      '/settings': (userRole === 'manager' || userRole === 'admin') && uiPreferences?.administrative?.settings,
      '/fee-management': (userRole === 'manager' || userRole === 'admin'),
      
      // Always accessible
      '/reset-password': uiPreferences?.general?.password_reset ?? true,
    };
    
    return featurePermissions[featurePath] ?? false;
  } catch (error) {
    console.error('Error checking feature access:', error);
    return featurePath === '/students' || featurePath === '/dashboard'; // Safe fallback
  }
};

// Get all menu items (don't filter, just mark as disabled)
const getMenuItems = () => {
  const userRole = localStorage.getItem('role');
  const isResponsibleTeacher = userRole === 'teacher' && 
    localStorage.getItem('isResponsibleTeacher') === 'true';
  
  const menuItems = [
    { 
      section: 'عام', 
      items: [
        { 
          text: 'الواجهة الرئيسية', 
          path: userRole === 'teacher' ? '/teacher-dashboard' : '/dashboard', 
          icon: <DashboardIcon />,
        },
      ]
    },
  ];
  
  // Financial section - only for manager/employee
  if (userRole === 'manager' || userRole === 'employee') {
    menuItems.push({
      section: 'المالية', 
      items: [
        { text: 'إدارة سندات الصرف', path: '/financial', icon: <AccountBalanceIcon /> },
        { text: 'إدارة سندات القبض', path: '/recipient', icon: <AccountBalanceIcon /> },
        { text: 'دفعات الطلاب', path: '/finance/cheques-status', icon: <AccountBalanceIcon /> },
        { text: 'دفعات المدرسة', path: '/finance/outgoingcheque', icon: <AccountBalanceIcon /> },
        { text: 'إدارة الرسوم', path: '/fee-management', icon: <AccountBalanceIcon /> },
      ]
    });
  }
  
  // School section - varies by role
  const schoolItems = [];
  
  // Students - only show for non-teachers or responsible teachers, but NOT for students
  if (userRole !== 'student' && (userRole !== 'teacher' || isResponsibleTeacher)) {
    schoolItems.push({ text: 'الطلاب', icon: <SchoolIcon />, path: '/students' });
  }
  
  // Add role-specific school items
  if (userRole === 'manager' || userRole === 'employee') {
    schoolItems.push(
      { text: 'الصفوف', path: '/classes', icon: <InventoryIcon /> },
      { text: 'الموظفين', icon: <PeopleIcon />, path: '/staff' },
      { text: 'الحافلات', path: '/buses', icon: <DirectionsBusIcon /> },
      { text: 'مبيعات المدرسة والمستودع', path: '/store-purchases', icon: <InventoryIcon /> },
      { text: 'متفرقات', path: '/miscellaneous', icon: <InventoryIcon /> }
    );
  }
  
  // Classes, Subjects, Schedule - accessible to manager, employee, teacher, student, parent
  // Classes - only show for non-teachers or responsible teachers, but NOT for students
  // Note: Already added for manager/employee above, so only add for teachers here
  if (userRole === 'teacher' && isResponsibleTeacher) {
     schoolItems.push({ text: 'الصفوف', path: '/classes', icon: <InventoryIcon /> });
  }
  
  // HIDDEN TEMPORARILY - المواد والمناوبون
  // schoolItems.push({ text: 'المواد', path: '/subjects', icon: <BookIcon /> });

  // Educational schedule - only when enabled for this account
  try {
    const uiPreferences = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
    if (uiPreferences?.school?.educational_schedule) {
      schoolItems.push({ text: 'جدول التعليمي', path: '/educational-schedule', icon: <CalendarMonthIcon /> });
    }
  } catch (e) {
    // Silently fail if preferences not available
  }

  // Substitute teachers - only for manager, employee (NOT teacher per requirement #7)
  if (userRole === 'manager' || userRole === 'employee') {
    // HIDDEN TEMPORARILY - المناوبون
    // schoolItems.push({ text: 'المناوبون', path: '/substitute-teachers', icon: <PeopleIcon /> });
    schoolItems.push({ text: 'الموردون', path: '/authorized-payers', icon: <StoreIcon /> });
  }
  
  // Library - check if enabled in UI preferences, but NOT for students
  if (userRole !== 'student') {
    try {
      const uiPreferences = JSON.parse(localStorage.getItem('uiPreferences') || '{}');
      if (uiPreferences?.school?.library) {
        schoolItems.push({ text: 'المكتبة', path: '/library', icon: <BookIcon /> });
      }
    } catch (e) {
      // Silently fail if preferences not available
    }
  }
  
  // Events - for manager, employee, teacher
  if (userRole === 'manager' || userRole === 'employee' || userRole === 'teacher') {
    schoolItems.push({ text: 'الفعاليات', path: '/events', icon: <CalendarMonthIcon /> });
  }
  
  // Archive - always last, only for manager/employee
  if (userRole === 'manager' || userRole === 'employee') {
    schoolItems.push({
      text: 'الأرشيف',
      icon: <ArchiveIcon />,
      children: [
        { text: 'الطلاب', path: '/archivestudents' },
        { text: 'الموظفين', path: '/archivestaff' }
      ],
    });
  }
  
  menuItems.push({
    section: 'المدرسة',
    items: schoolItems
  });
  
  // Administrative section - only for manager/admin
  if (userRole === 'manager' || userRole === 'admin') {
    menuItems.push({
      section: 'إداري', 
      items: [
{ text: 'تقارير', path: '/reports', icon: <InventoryIcon /> },
        { text: 'سجل التغييرات', path: '/admin/logs', icon: <InventoryIcon /> },
        { text: 'انتقال السنة الدراسية', path: '/admin/year-transition', icon: <CalendarMonthIcon /> },
        { text: 'الإعدادات', path: '/settings', icon: <SettingsIcon /> },
      ]
    });
  }

  return menuItems;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSections, setOpenSections] = useState([]);
  const [openMenus, setOpenMenus] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const { currentStep } = useGuide() ?? {};

  // Auto-expand the section that contains the guide's target item
  useEffect(() => {
    if (currentStep?.section) {
      setOpenSections((prev) =>
        prev.includes(currentStep.section) ? prev : [...prev, currentStep.section]
      );
    }
  }, [currentStep]);

  // Get all menu items
  const menuItems = getMenuItems();

  const getGuideId = (path) => {
    const map = {
      '/students':               'guide-students',
      '/classes':                'guide-classes',
      '/staff':                  'guide-staff',
      '/buses':                  'guide-buses',
      '/store':                  'guide-store',
      '/events':                 'guide-events',
      '/library':                'guide-library',
      '/authorized-payers':      'guide-authorized-payers',
      '/financial':              'guide-financial',
      '/recipient':              'guide-recipient',
      '/finance/cheques-status': 'guide-cheques',
      '/fee-management':         'guide-fee-management',
      '/admin/users':            'guide-admin-users',
      '/reports':                'guide-reports',
      '/settings':               'guide-settings',
      '/admin/year-transition':  'guide-year-transition',
    };
    return map[path];
  };

  const guideItemStyle = (path) => {
    const id = getGuideId(path);
    if (!id || currentStep?.target !== id) return {};
    return {
      animation: `${guidePulse} 1.4s ease-in-out infinite`,
      background: 'rgba(0,191,255,0.24)',
      position: 'relative',
      zIndex: 1201,
    };
  };

  const handleSectionClick = (section) => {
    setOpenSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const handleMainClick = (item) => {
    // Check if item is disabled
    if (item.disabled) {
      return; // Don't navigate if disabled
    }
    
    // Check if item is accessible
    const isAccessible = canAccessFeature(item.path);
    
    if (!isAccessible && !item.children) {
      // Show tooltip or do nothing for disabled items
      return;
    }

    if (item.children) {
      setOpenMenus((prev) => ({
        ...prev,
        [item.text]: !prev[item.text]
      }));
      if (item.path && isAccessible) {
        navigate(item.path);
      }
    } else if (item.path && isAccessible) {
      navigate(item.path);
    }
  };

  const handleChildClick = (child) => {
    const isAccessible = canAccessFeature(child.path);
    if (isAccessible) {
      navigate(child.path);
    }
  };

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // محتوى الشريط الجانبي
  const drawerContent = (
    <Box sx={{
      px: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      // سكرول‑بار رفيع بلون الهوية بدل الرمادي العريض الافتراضي
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(255,255,255,0.22) transparent',
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255,255,255,0.18)',
        borderRadius: '8px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(0,191,255,0.55)',
      },
    }}>
      {/* زر إغلاق يظهر فقط على الشاشات الصغيرة */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end', alignItems: 'center', px: 1, pt: 1 }}>
        <CloseIcon
          onClick={handleDrawerToggle}
          sx={{
            fontSize: 28,
            color: '#4338CA',
            bgcolor: '#fff',
            borderRadius: '50%',
            boxShadow: '0 2px 8px 0 rgba(77,76,172,0.10)',
            cursor: 'pointer',
            p: 0.5
          }}
        />
      </Box>
      <Box>
        {/* Modern Logo/User Section */}
        <Box sx={{
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 1.5,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 3,
          mx: 1
        }}>
          <Avatar
            sx={{
              width: 150,
              height: 150,
              bgcolor: '#fff',
              mb: 1,
              fontWeight: 700,
              fontSize: 34,
              color: '#4338CA',
              border: '2px solid rgba(255,255,255,0.6)',
              boxShadow: '0 2px 10px 0 rgba(0,191,255,0.35)'
            }}
            src="/شعار دفتر نون.png"
            alt="شعار دفتر نون"
          />
        </Box>
        <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
        <List sx={{ px: 0.5 }}>
          {menuItems.map((sectionObj, idx) => (
            <React.Fragment key={sectionObj.section}>
              {idx !== 0 && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />}
              {sectionObj.section !== 'عام' && (
                <ListItemButton
                  onClick={() => handleSectionClick(sectionObj.section)}
                  sx={{
                    borderRadius: 2,
                    mx: 0.2,
                    my: 0.2,
                    color: '#bfc9e0',
                    background: openSections.includes(sectionObj.section) ? 'rgba(255,255,255,0.06)' : 'transparent',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.10)',
                    },
                    transition: 'background 0.2s',
                    minHeight: 40,
                    px: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#38BDF8',
                      fontWeight: 800,
                      fontSize: 16,
                      flex: 1,
                      letterSpacing: 1,
                      textAlign: 'right',
                      userSelect: 'none',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  >
                    {sectionObj.section}
                  </Typography>
                  {openSections.includes(sectionObj.section) ? (
                    <ExpandLessIcon sx={{ color: '#38BDF8', fontSize: 22 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: '#38BDF8', fontSize: 22 }} />
                  )}
                </ListItemButton>
              )}
              {sectionObj.section === 'عام' ? (
                <>{sectionObj.items.map((item) => {
                  const isAccessible = canAccessFeature(item.path);
                  const hasAccessibleChildren = item.children ? item.children.some(child => canAccessFeature(child.path)) : false;
                  const showChildren = item.children && (isAccessible || hasAccessibleChildren);
                  
                  return (
                    <React.Fragment key={item.text}>
                      <Tooltip 
                        title={
                          item.disabled 
                            ? "غير متاح للمعلمين غير المسؤولين" 
                            : !isAccessible 
                            ? "ليس لديك صلاحية للوصول إلى هذه الصفحة" 
                            : item.text
                        } 
                        placement="left" 
                        arrow
                      >
                        <ListItemButton
                          onClick={() => handleMainClick(item)}
                          selected={
                            item.children
                              ? item.children.some(child => location.pathname === child.path)
                              : location.pathname === item.path
                          }
                          disabled={(item.disabled || (!isAccessible && !item.children))}
                          data-guide-id={getGuideId(item.path)}
                          sx={{
                            borderRadius: 2,
                            mx: 0.2,
                            my: 0.2,
                            color: 'inherit',
                            background: (item.children
                              ? item.children.some(child => location.pathname === child.path)
                              : location.pathname === item.path)
                              ? 'rgba(0,191,255,0.16)' : 'transparent',
                            '&:hover': {
                              background: isAccessible || item.children ? 'rgba(255,255,255,0.10)' : 'transparent',
                            },
                            transition: 'background 0.2s',
                            minHeight: 42,
                            px: 1.2,
                            opacity: (item.disabled || (!isAccessible && !item.children)) ? 0.5 : 1,
                            cursor: (item.disabled || (!isAccessible && !item.children)) ? 'not-allowed' : 'pointer',
                            ...guideItemStyle(item.path),
                          }}
                        >
                          {(!item.children && item.icon) && (
                            <ListItemIcon sx={{
                              minWidth: 30,
                              fontSize: 22,
                              color: !isAccessible ? '#666' :
                                sectionObj.section === 'المالية' ? '#34D399' :
                                sectionObj.section === 'المدرسة' ? '#38BDF8' :
                                sectionObj.section === 'إداري' ? '#FBBF24' :
                                '#C7D2FE',
                            }}>
                              {!isAccessible ? <LockIcon /> : item.icon}
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={item.text}
                            sx={{
                              textAlign: 'right',
                              fontWeight: 600,
                              fontSize: '1.05rem',
                              fontFamily: 'Cairo, Tajawal, sans-serif',
                              color: !isAccessible && !item.children ? '#888' : '#fff',
                            }}
                          />
                          {item.children && (
                            openMenus[item.text] ?
                            <ExpandLessIcon sx={{ color: '#bfc9e0', fontSize: 20 }} /> :
                            <ExpandMoreIcon sx={{ color: '#bfc9e0', fontSize: 20 }} />
                          )}
                          {(!isAccessible && !item.children) && (
                            <LockIcon sx={{ color: '#666', fontSize: 18, ml: 1 }} />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      {showChildren && (
                        <Collapse in={!!openMenus[item.text]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {item.children.map((child) => {
                              const childAccessible = canAccessFeature(child.path);
                              return (
                                <Tooltip
                                  key={child.text}
                                  title={!childAccessible ? "ليس لديك صلاحية للوصول إلى هذه الصفحة" : child.text}
                                  placement="left"
                                  arrow
                                >
                                  <ListItemButton
                                    onClick={() => handleChildClick(child)}
                                    selected={location.pathname === child.path}
                                    disabled={!childAccessible}
                                    sx={{
                                      pl: 4,
                                      mx: 0.5,
                                      my: 0.2,
                                      color: 'inherit',
                                      background: location.pathname === child.path ? 'rgba(0,191,255,0.24)' : 'transparent',
                                      '&:hover': {
                                        background: childAccessible ? 'rgba(0,191,255,0.16)' : 'transparent',
                                      },
                                      borderRadius: 2,
                                      transition: 'background 0.2s',
                                      minHeight: 36,
                                      opacity: !childAccessible ? 0.5 : 1,
                                      cursor: !childAccessible ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    <ListItemText
                                      primary={child.text}
                                      sx={{
                                        textAlign: 'right',
                                        fontSize: '0.98rem',
                                        fontFamily: 'Cairo, Tajawal, sans-serif',
                                        color: !childAccessible ? '#888' : '#e0e6f7',
                                        fontWeight: 500,
                                      }}
                                    />
                                    {!childAccessible && (
                                      <LockIcon sx={{ color: '#666', fontSize: 16, ml: 1 }} />
                                    )}
                                  </ListItemButton>
                                </Tooltip>
                              );
                            })}
                          </List>
                        </Collapse>
                      )}
                    </React.Fragment>
                  );
                })}</>
              ) : (
                <Collapse in={openSections.includes(sectionObj.section)} timeout="auto" unmountOnExit>
                  {sectionObj.items.map((item) => {
                  const isAccessible = canAccessFeature(item.path);
                  const hasAccessibleChildren = item.children ? item.children.some(child => canAccessFeature(child.path)) : false;
                  const showChildren = item.children && (isAccessible || hasAccessibleChildren);

                  return (
                    <React.Fragment key={item.text}>
                      <Tooltip
                        title={
                          item.disabled
                            ? "غير متاح للمعلمين غير المسؤولين"
                            : !isAccessible
                            ? "ليس لديك صلاحية للوصول إلى هذه الصفحة"
                            : item.text
                        }
                        placement="left"
                        arrow
                      >
                        <ListItemButton
                          onClick={() => handleMainClick(item)}
                          selected={
                            item.children
                              ? item.children.some(child => location.pathname === child.path)
                              : location.pathname === item.path
                          }
                          disabled={(item.disabled || (!isAccessible && !item.children))}
                          data-guide-id={getGuideId(item.path)}
                          sx={{
                            borderRadius: 2,
                            mx: 0.2,
                            my: 0.2,
                            color: 'inherit',
                            background: (item.children
                              ? item.children.some(child => location.pathname === child.path)
                              : location.pathname === item.path)
                              ? 'rgba(0,191,255,0.16)' : 'transparent',
                            '&:hover': {
                              background: isAccessible || item.children ? 'rgba(255,255,255,0.10)' : 'transparent',
                            },
                            transition: 'background 0.2s',
                            minHeight: 42,
                            px: 1.2,
                            opacity: (item.disabled || (!isAccessible && !item.children)) ? 0.5 : 1,
                            cursor: (item.disabled || (!isAccessible && !item.children)) ? 'not-allowed' : 'pointer',
                            ...guideItemStyle(item.path),
                          }}
                        >
                          {(!item.children && item.icon) && (
                            <ListItemIcon sx={{
                              minWidth: 30,
                              fontSize: 22,
                              color: !isAccessible ? '#666' :
                                sectionObj.section === 'المالية' ? '#34D399' :
                                sectionObj.section === 'المدرسة' ? '#38BDF8' :
                                sectionObj.section === 'إداري' ? '#FBBF24' :
                                '#C7D2FE',
                            }}>
                              {!isAccessible ? <LockIcon /> : item.icon}
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={item.text}
                            sx={{
                              textAlign: 'right',
                              fontWeight: 600,
                              fontSize: '1.05rem',
                              fontFamily: 'Cairo, Tajawal, sans-serif',
                              color: !isAccessible && !item.children ? '#888' : '#fff',
                            }}
                          />
                          {item.children && (
                            openMenus[item.text] ?
                            <ExpandLessIcon sx={{ color: '#bfc9e0', fontSize: 20 }} /> :
                            <ExpandMoreIcon sx={{ color: '#bfc9e0', fontSize: 20 }} />
                          )}
                          {(!isAccessible && !item.children) && (
                            <LockIcon sx={{ color: '#666', fontSize: 18, ml: 1 }} />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      {showChildren && (
                        <Collapse in={!!openMenus[item.text]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {item.children.map((child) => {
                              const childAccessible = canAccessFeature(child.path);
                              return (
                                <Tooltip
                                  key={child.text}
                                  title={!childAccessible ? "ليس لديك صلاحية للوصول إلى هذه الصفحة" : child.text}
                                  placement="left"
                                  arrow
                                >
                                  <ListItemButton
                                    onClick={() => handleChildClick(child)}
                                    selected={location.pathname === child.path}
                                    disabled={!childAccessible}
                                    sx={{
                                      pl: 4,
                                      mx: 0.5,
                                      my: 0.2,
                                      color: 'inherit',
                                      background: location.pathname === child.path ? 'rgba(0,191,255,0.24)' : 'transparent',
                                      '&:hover': {
                                        background: childAccessible ? 'rgba(0,191,255,0.16)' : 'transparent',
                                      },
                                      borderRadius: 2,
                                      transition: 'background 0.2s',
                                      minHeight: 36,
                                      opacity: !childAccessible ? 0.5 : 1,
                                      cursor: !childAccessible ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    <ListItemText
                                      primary={child.text}
                                      sx={{
                                        textAlign: 'right',
                                        fontSize: '0.98rem',
                                        fontFamily: 'Cairo, Tajawal, sans-serif',
                                        color: !childAccessible ? '#888' : '#e0e6f7',
                                        fontWeight: 500,
                                      }}
                                    />
                                    {!childAccessible && (
                                      <LockIcon sx={{ color: '#666', fontSize: 16, ml: 1 }} />
                                    )}
                                  </ListItemButton>
                                </Tooltip>
                              );
                            })}
                          </List>
                        </Collapse>
                      )}
                    </React.Fragment>
                  );
                })}
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
      <Box>
        {/* زر تعيين كلمة مرور جديدة منفصل */}
        <Box sx={{ px: 1.5, mt: 1.5 }}>
          <Tooltip 
            title={!canAccessFeature('/reset-password') ? "ليس لديك صلاحية للوصول إلى هذه الصفحة" : "تعيين كلمة مرور جديدة"}
            placement="left"
            arrow
          >
            <ListItemButton
              onClick={() => canAccessFeature('/reset-password') && navigate('/reset-password')}
              disabled={!canAccessFeature('/reset-password')}
              sx={{
                borderRadius: 2,
                color: '#fff',
                background: canAccessFeature('/reset-password')
                  ? 'linear-gradient(90deg, #00BFFF 0%, #4338CA 100%)'
                  : 'linear-gradient(90deg, #666 0%, #888 100%)',
                fontWeight: 'bold',
                boxShadow: '0 2px 10px 0 rgba(0,191,255,0.25)',
                mb: 1,
                minHeight: 38,
                opacity: canAccessFeature('/reset-password') ? 1 : 0.5,
                cursor: canAccessFeature('/reset-password') ? 'pointer' : 'not-allowed',
                '&:hover': {
                  background: canAccessFeature('/reset-password')
                    ? 'linear-gradient(90deg, #4338CA 0%, #00BFFF 100%)'
                    : 'linear-gradient(90deg, #666 0%, #888 100%)',
                  color: '#fff'
                },
                transition: 'background 0.2s'
              }}
            >
              <ListItemIcon sx={{ color: '#fff', minWidth: 30 }}>
                {canAccessFeature('/reset-password') ? <SettingsIcon fontSize="small" /> : <LockIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary="تعيين كلمة مرور جديدة"
                sx={{
                  textAlign: 'right',
                  fontWeight: 700,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  fontSize: '0.98rem'
                }}
              />
            </ListItemButton>
          </Tooltip>
        </Box>
        {/* زر تسجيل الخروج */}
        <Box sx={{ px: 1.5, mt: 1.5 }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              color: '#fff',
              background: 'linear-gradient(90deg, #ff5858 0%, #f09819 100%)',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px 0 rgba(255,88,88,0.10)',
              mb: 1,
              minHeight: 38,
              '&:hover': {
                background: 'linear-gradient(90deg, #f09819 0%, #ff5858 100%)',
                color: '#fff'
              },
              transition: 'background 0.2s'
            }}
          >
            <ListItemIcon sx={{ color: '#fff', minWidth: 30 }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="تسجيل الخروج"
              sx={{
                textAlign: 'right',
                fontWeight: 700,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                fontSize: '0.98rem'
              }}
            />
          </ListItemButton>
        </Box>
        <Divider sx={{ mt: 1.5, borderColor: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ py: 1.5, textAlign: 'center', color: '#bfc9e0', fontSize: 12 }}>
          <Typography variant="caption" sx={{ color: '#bfc9e0' }}>
            &copy; {new Date().getFullYear()} دفتر نون
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* زر burger يظهر فقط على الشاشات الصغيرة */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, position: 'fixed', top: 16, right: 16, zIndex: 1301 }}>
        <MenuIcon
          onClick={handleDrawerToggle}
          sx={{
            fontSize: 32,
            color: '#4338CA',
            bgcolor: '#fff',
            borderRadius: '50%',
            boxShadow: '0 2px 8px 0 rgba(77,76,172,0.10)',
            cursor: 'pointer',
            p: 0.7
          }}
        />
      </Box>
      {/* Drawer دائم على الشاشات الكبيرة */}
      <Drawer
        variant="permanent"
        anchor="right"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #1E1B4B 0%, #312E81 55%, #3730A3 100%)',
            color: '#ecf0f1',
            borderLeft: 'none',
            px: 0,
            boxShadow: '0 2px 16px 0 rgba(44, 62, 80, 0.13)',
            overflow: 'hidden',
            minHeight: '100vh',
            maxHeight: '100vh',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 72 }} />
        {drawerContent}
      </Drawer>
      {/* Drawer متحرك على الشاشات الصغيرة */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: mobileDrawerWidth, sm: drawerWidth },
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #1E1B4B 0%, #312E81 55%, #3730A3 100%)',
            color: '#ecf0f1',
            borderLeft: 'none',
            px: 0,
            boxShadow: '0 2px 16px 0 rgba(44, 62, 80, 0.13)',
            overflow: 'hidden',
            minHeight: '100vh',
            maxHeight: '100vh',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 72 }} />
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
